import { useEffect, useState } from "react";
import { List } from "@refinedev/antd";
import { useDelete, useOne, useUpdate } from "@refinedev/core";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Select,
  Space,
  Typography,
} from "antd";
import dayjs, { Dayjs } from "dayjs";

import {
  TV_LEAGUES,
  TvMatchContent,
  TvMatchRef,
  tvErrorNotification,
  tvSuccessNotification,
} from "../Lib";
import { MatchPicker, TvImageField, matchLabel } from "../Components";

type FormValues = {
  previewText?: string;
  coverUrl?: string;
  cardBgUrl?: string;
  liveFrom?: Dayjs | null;
  liveTo?: Dayjs | null;
};

/** Превью, обложка, фон карточки и окно эфира матча для ФНЛ ТВ. Матч выбирается поиском. */
export const TvMatchesPage = () => {
  const [leagueId, setLeagueId] = useState<number>(100);
  const [matchId, setMatchId] = useState<number | null>(null);
  const [match, setMatch] = useState<TvMatchRef>();
  const [form] = Form.useForm<FormValues>();
  const key = matchId ? `${leagueId}/${matchId}` : "";

  const { data, isFetching, refetch } = useOne<TvMatchContent>({
    resource: "tv-matches",
    dataProviderName: "tvDataProvider",
    id: key,
    queryOptions: { enabled: Boolean(matchId) },
    ...tvErrorNotification("Не удалось загрузить"),
  });
  const content = matchId ? data?.data : undefined;
  const { mutate: update, isLoading: saving } = useUpdate();
  const { mutate: remove, isLoading: removing } = useDelete();

  useEffect(() => {
    form.setFieldsValue({
      previewText: content?.previewText ?? undefined,
      coverUrl: content?.coverUrl ?? undefined,
      cardBgUrl: content?.cardBgUrl ?? undefined,
      liveFrom: content?.liveFrom ? dayjs(content.liveFrom) : null,
      liveTo: content?.liveTo ? dayjs(content.liveTo) : null,
    });
  }, [content, form]);

  const save = (v: FormValues) =>
    update(
      {
        resource: "tv-matches",
        dataProviderName: "tvDataProvider",
        id: key,
        values: {
          previewText: v.previewText || null,
          coverUrl: v.coverUrl || null,
          cardBgUrl: v.cardBgUrl || null,
          liveFrom: v.liveFrom ? v.liveFrom.toISOString() : null,
          liveTo: v.liveTo ? v.liveTo.toISOString() : null,
        },
        ...tvSuccessNotification("Данные матча сохранены"),
        ...tvErrorNotification(),
      },
      { onSuccess: () => refetch() }
    );

  const clear = () =>
    remove(
      {
        resource: "tv-matches",
        dataProviderName: "tvDataProvider",
        id: key,
        ...tvSuccessNotification("Данные матча очищены"),
        ...tvErrorNotification(),
      },
      { onSuccess: () => refetch() }
    );

  return (
    <List
      title="ФНЛ ТВ — матчи"
      resource="tv-matches"
      breadcrumb={false}
      canCreate={false}
    >
      <Typography.Paragraph type="secondary">
        Для матча на ФНЛ ТВ: обложка, фон карточки и текст превью (если превью
        пустое, показывается описание матча с сайта). Окно эфира нужно, только
        если студия выходит до стартового свистка или работает после финального
        — иначе эфир идёт по событиям матча.
      </Typography.Paragraph>
      <Space.Compact style={{ width: "100%", marginBottom: 24 }}>
        <Select
          style={{ width: 240 }}
          value={leagueId}
          options={TV_LEAGUES}
          onChange={(v) => {
            setLeagueId(v);
            setMatchId(null);
            setMatch(undefined);
          }}
        />
        <div style={{ flex: 1 }}>
          <MatchPicker
            leagueId={leagueId}
            value={matchId}
            onChange={(v, m) => {
              setMatchId(v);
              setMatch(m);
            }}
          />
        </div>
      </Space.Compact>

      {matchId ? (
        <Card
          title={match ? matchLabel(match) : `Матч №${matchId}`}
          loading={isFetching && !content}
        >
          <Form form={form} layout="vertical" onFinish={save}>
            <Row gutter={32}>
              <Col xs={24} xl={14}>
                <Form.Item
                  name="previewText"
                  label="Превью матча"
                  rules={[{ max: 5000 }]}
                >
                  <Input.TextArea autoSize={{ minRows: 5, maxRows: 14 }} />
                </Form.Item>
                <Typography.Title level={5}>Окно эфира</Typography.Title>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name="liveFrom" label="Начало эфира">
                      <DatePicker
                        showTime
                        format="DD.MM.YYYY HH:mm"
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="liveTo"
                      label="Конец эфира"
                      dependencies={["liveFrom"]}
                      rules={[
                        ({ getFieldValue }) => ({
                          validator: (_, value) => {
                            const from: Dayjs | null =
                              getFieldValue("liveFrom");
                            if (!value || !from) return Promise.resolve();
                            if (!from.isBefore(value))
                              return Promise.reject(
                                new Error("Конец должен быть позже начала")
                              );
                            if (value.diff(from, "hour", true) > 24)
                              return Promise.reject(
                                new Error("Эфир — не дольше суток")
                              );
                            return Promise.resolve();
                          },
                        }),
                      ]}
                    >
                      <DatePicker
                        showTime
                        format="DD.MM.YYYY HH:mm"
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Col>
              <Col xs={24} xl={10}>
                <Form.Item
                  name="coverUrl"
                  label="Обложка 16:9"
                  extra="Шапка страницы матча, превью ссылки и личная витрина"
                >
                  <TvImageField label="Обложка матча" />
                </Form.Item>
                <Form.Item
                  name="cardBgUrl"
                  label="Фон карточки 16:9"
                  extra="Карточка матча на главной, в трансляциях лиги, на странице клуба и в поиске — вместо паттерна лиги; обзор и запись матча без своей обложки — тоже. Гербы, команды и дата — поверх, на фоне их не рисовать"
                >
                  <TvImageField label="Фон карточки" />
                </Form.Item>
              </Col>
            </Row>
            <Space>
              <Button type="primary" htmlType="submit" loading={saving}>
                Сохранить
              </Button>
              {content?.updatedAt && (
                <Button danger onClick={clear} loading={removing}>
                  Очистить
                </Button>
              )}
            </Space>
          </Form>
        </Card>
      ) : (
        <Alert
          type="info"
          showIcon
          message="Выберите матч: начните вводить название клуба или номер матча"
        />
      )}
    </List>
  );
};
